import type {
  CompositionV3BreakdownOptions,
  CompositionV3Ent,
  CompositionV3Req,
} from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  FilterType,
  isSegmentedFilter,
  type PrimaryFilterId,
  type TreeFilter,
} from '../../types/filters';
import { flattenMultiFiltersForTooltip } from '../flattenMultiFiltersForTooltip';
import { getV3Entities } from '../get-v3-entities';
import { getV3Taxonomies } from '../get-v3-taxonomies';
import { stripEntitiesFromFilters } from '../role-skill-ent-kind';
import { getCompositionV3BreakdownOptions } from './getCompositionV3BreakdownOptions';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from './getCompositionV3Filters';

/**
 * Aligns composition encoding with `useEffectiveFilters` output: merges
 * `MULTI_*` tree instances (same as tooltip flattening) and unwraps
 * `SegmentedFilter` wrappers so `FilterType.TREE` handlers see role/skill/RICS
 * selections.
 */
export const normalizeCompositionEffectiveFilters = (
  effectiveFilters: Filter[]
): Filter[] => {
  const flattened = flattenMultiFiltersForTooltip(effectiveFilters);
  return flattened.map((filter) => {
    if (
      isSegmentedFilter(filter) &&
      filter.wrappedFilter.type === FilterType.TREE
    ) {
      return filter.wrappedFilter;
    }
    return filter;
  });
};

export const getCompositionV3Variables = ({
  primaryDimension,
  getSelectionBreakdowns: _getSelectionBreakdowns = false,
  isSnapshot = false,
  effectiveFilters,
  customSkillTree,
  breakdownOptionsOverride,
  geographySelectionListIds,
  compositionEntityScopes,
  activeFilterMap,
}: {
  primaryDimension: PrimaryFilterId;
  getSelectionBreakdowns?: boolean;
  isSnapshot?: boolean;
  effectiveFilters: Filter[];
  activeFilterMap?: ReadonlyMap<FilterId, Filter>;
  /** When set (e.g. chart `entitiesOverride`), these scopes replace primary `entities`. */
  compositionEntityScopes?: CompositionV3Ent[] | null;
  customSkillTree?:
    | { id: string; name: string; depth: number; children?: unknown[] }[]
    | null;
  breakdownOptionsOverride?: CompositionV3BreakdownOptions;
  geographySelectionListIds?: readonly string[];
}): CompositionV3Req => {
  const filtersToEncode =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const filtersFromState = getCompositionV3Filters({
    primaryDimension,
    isSnapshot,
    effectiveFilters: filtersToEncode,
  });

  const entitiesFromPrimary = getV3Entities({
    primaryDimension,
    filters: filtersToEncode,
  });

  const entities =
    compositionEntityScopes !== undefined && compositionEntityScopes !== null
      ? compositionEntityScopes
      : entitiesFromPrimary;

  const filters = stripEntitiesFromFilters(filtersFromState, entities);

  const corrections = getCompositionV3Corrections(
    filtersToEncode,
    activeFilterMap
  );

  const { role_taxonomy, skill_taxonomy, activity_taxonomy } = getV3Taxonomies(
    filtersToEncode,
    activeFilterMap
  );

  const breakdown_options =
    breakdownOptionsOverride ??
    getCompositionV3BreakdownOptions({
      compositionFilters: filters,
      customSkillTree,
      customSkillDeepestLevel:
        getCustomSkillDeepestLevelFromFilters(filtersToEncode),
      geographySelectionListIds,
    });

  return {
    entities,
    filters,
    corrections,
    role_taxonomy,
    skill_taxonomy,
    activity_taxonomy,
    breakdown_options,
  };
};

export const getCustomSkillDeepestLevelFromFilters = (
  effectiveFilters: Filter[]
): 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4' | null => {
  const cs = effectiveFilters.find(
    (f) => f.id === FilterId.CUSTOM_SKILL && f.type === FilterType.TREE
  ) as TreeFilter | undefined;
  if (!cs) return null;
  const order: Record<string, 0 | 1 | 2 | 3 | 4> = {
    skill_0: 0,
    skill_1: 1,
    skill_2: 2,
    skill_3: 3,
    skill_4: 4,
  };
  let deepest: 0 | 1 | 2 | 3 | 4 | null = null;
  cs.selections.forEach((s) => {
    const lv = order[s.levelId as string];
    if (lv === undefined) return;
    if (deepest === null || lv > deepest) deepest = lv;
  });
  if (deepest === null) return null;
  const keys = ['skill_0', 'skill_1', 'skill_2', 'skill_3', 'skill_4'] as const;
  const key = keys[deepest];
  return key ?? null;
};
